import type { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import type { ChartDataset } from '../Charts'
import type { Document } from '@contentful/rich-text-types'
import type { TimeUnit } from 'chart.js'
import type { TickFormat } from '../Charts/MultiChart/types'

export type ContentfulXAxisOptions = {
  axisLabel?: string
  isOffset?: boolean
  maxValue?: number
  minValue?: number
  timeUnit?: TimeUnit
  __typename: string
}

export type ContentfulYAxisOptions = {
  axisLabel?: string
  isOffset?: boolean
  maxValue?: number
  minValue?: number
  tickFormat?: TickFormat
  __typename: string
}

export type ContentfulBarChart = {
  chartId: string
  label: string
  backgroundColors: Array<string> | null
  query?: string
  staticData?: Array<ChartDataset>
  isRightYAxis?: boolean
  __typename: 'BarChart'
}

export type ContentfulLineChart = {
  chartId: string
  label: string
  borderColors: Array<string> | null
  query?: string
  staticData?: Array<ChartDataset>
  connectedChart?: Pick<ContentfulLineChart, 'chartId' | '__typename'>
  isRightYAxis?: boolean
  __typename: 'LineChart'
}

// Would extend
export type ContentfulChart = ContentfulBarChart | ContentfulLineChart

export interface ContentfulResponse {
  chartCollection?: {
    items?: Array<ContentfulChartConfig>
  }
}

export interface ContentfulChartConfig {
  title: string
  showTitle: boolean
  query: string
  description: {
    json: Document
  }
  enableFullscreen: boolean
  enableFiltering: boolean
  enableDownload: boolean
  enableTableView: boolean
  showNoDataOverlay: boolean
  showGridLines: boolean
  legendPosition?: 'none' | 'left' | 'right' | 'top' | 'bottom'
  xAxisOptions?: ContentfulXAxisOptions
  yAxisOptions?: ContentfulYAxisOptions
  y1AxisOptions?: ContentfulYAxisOptions
  chartTypeCollection: {
    items: Array<ContentfulChart>
  }
}

export interface ChartWidgetProps {
  /**
   * Unique identifier for the Contentful chart entry.
   */
  id: string

  /**
   * Apollo Client instance used to fetch chart data via a custom GraphQL query,
   * if such a query is specified in the Contentful chart entry.
   */
  chartDataClient: ApolloClient<NormalizedCacheObject>

  /**
   * Optional local data for the chart. This data will be used if no custom
   * query is provided. If a query is specified in the Contentful chart entry,
   * this local data will be ignored.
   */
  localData?: Record<string, Array<ChartDataset>>

  /**
   * Optional variables to pass to the custom GraphQL query for fetching chart
   * data.
   */
  chartDataQueryVariables?: Record<string, any>

  /**
   * Optional component to render after the title.
   */
  titleSuffixComponent?: React.ReactNode
}

export enum ViewMode {
  Chart = 'Chart',
  Table = 'Table',
}
